import { useDispatch, useSelector } from 'react-redux';
import {
  fetchClientByClientIdRequest,
  fetchTypeformPayloadByClientIdRequest,
  selectClientDataExists,
  selectClientIsLoading,
  selectMaybeClient,
  selectTypeformPayload,
  selectTypeformPayloadIsLoading,
  SheetModal,
} from '..';
import { useEffect, useMemo, useState } from 'react';
import { fullName, ClientStatus, QUICK_EDIT_ALL_FIELDS } from '@shared';
import { useNavigateToIntakeForm } from '@client/hooks/useNavigateToIntakeForm';
import { useQuickEditIntake } from '@client/hooks/useQuickEditIntake';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { Button } from './ui/button';
import { Loader2, Pencil } from 'lucide-react';
import { QuickEditForm, QUICK_EDIT_FORM_ID } from './QuickEditForm';

import clientIntakeFormSchema from '../util/clientIntakeFormSchema.json';
import { isArray } from 'lodash';

export function ClientDataModal({
  partialClient,
  open,
  initialMode = 'view',
  onClose,
}: {
  partialClient: Client;
  open: boolean;
  initialMode?: 'view' | 'edit';
  onClose: () => void;
}) {
  const dispatch = useDispatch();
  const clientIsLoading = useSelector(selectClientIsLoading);
  const clientDataExists = useSelector(selectClientDataExists);
  const typeformPayload = useSelector(selectTypeformPayload);
  const typeformPayloadIsLoading = useSelector(selectTypeformPayloadIsLoading);
  const { navigateToIntakeForm, isLoading: isLoadingForm } = useNavigateToIntakeForm({
    clientId: partialClient.clientId,
  });
  const { flags } = useFeatureFlags();
  const useFormProvider = flags.useFormProvider ?? false;

  const isClientActive = partialClient.clientStatus === ClientStatus.active;

  const hasIntakeSurvey = (!!partialClient.intakeSurvey || isClientActive) && !!partialClient.formReferenceId;
  const { mode, setMode, save, isPending: isQuickEditPending, isRefreshing, clearRefreshing } = useQuickEditIntake({
    clientId: partialClient.clientId,
  });

  const clientName = fullName(partialClient);
  const [isQuickEditValid, setIsQuickEditValid] = useState(true);

  const clientDataReady =
    clientDataExists &&
    !clientIsLoading &&
    !typeformPayloadIsLoading &&
    (!typeformPayload || typeformPayload.clientId === partialClient.clientId);

  useEffect(() => {
    if (isRefreshing && clientDataReady) {
      clearRefreshing();
    }
  }, [isRefreshing, clientDataReady, clearRefreshing]);

  useEffect(() => {
    if (open) {
      setMode(initialMode);
      dispatch(fetchClientByClientIdRequest(partialClient.clientId));
      dispatch(fetchTypeformPayloadByClientIdRequest({ clientId: partialClient.clientId }));
    }
  }, [open, partialClient.clientId, dispatch, initialMode, setMode]);

  const handleClose = () => {
    setMode('view');
    onClose();
  };

  const title =
    mode === 'edit'
      ? 'Quick Edit Mode'
      : clientName ?? 'Client Information';

  const subTitle =
    mode === 'edit'
      ? 'Edit commonly updated fields below. Only a subset of intake fields can be modified here.'
      : `Information provided in ${clientName ? `${clientName}'s ` : 'the '} intake form.`;

  const primaryButton =
    mode === 'edit' ? (
      <div className="flex w-full flex-col">
        <div className="border-b pb-3 text-center">
          <button
            type="button"
            onClick={async () => { await navigateToIntakeForm(); handleClose(); }}
            disabled={isLoadingForm}
            className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:text-primary-hover underline underline-offset-2 disabled:opacity-50"
          >
            {isLoadingForm ? 'Loading...' : (
              <>
                Need to make additional changes? Access your full intake questionnaire
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="inline-block"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
              </>
            )}
          </button>
        </div>
        <div className="flex gap-2 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => setMode('view')}
            disabled={isQuickEditPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form={QUICK_EDIT_FORM_ID}
            disabled={isQuickEditPending || !isQuickEditValid}
          >
            {isQuickEditPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </div>
    ) : hasIntakeSurvey ? (
      <Button
        type="button"
        onClick={() => setMode('edit')}
      >
        <Pencil className="mr-2 h-4 w-4" />
        {`Edit ${clientName ? `${clientName}'s` : 'Client'} Data`}
      </Button>
    ) : undefined;

  return (
    <SheetModal
      open={open}
      onClose={handleClose}
      title={title}
      subTitle={subTitle}
      className="min-w-[95%] sm:min-w-[90%] md:min-w-[700px] lg:min-w-[900px] xl:min-w-[1000px]"
      primaryButton={primaryButton}
    >
      <ClientDataModalContent
        mode={mode}
        clientDataReady={clientDataReady && !isRefreshing}
        onCancelEdit={() => setMode('view')}
        onSave={save}
        onValidChange={setIsQuickEditValid}
      />
    </SheetModal>
  );
}

function ClientDataModalContent({
  mode,
  clientDataReady,
  onCancelEdit,
  onSave,
  onValidChange,
}: {
  mode: 'view' | 'edit';
  clientDataReady: boolean;
  onCancelEdit: () => void;
  onSave: (fields: Record<string, string | number | null>) => void;
  onValidChange: (valid: boolean) => void;
}) {
  const client = useSelector(selectMaybeClient);

  if (!clientDataReady || !client) {
    return <ClientDataDescriptionList isLoading={true} />;
  }

  if (mode === 'edit' && client.intakeSurvey) {
    return (
      <QuickEditForm
        intakeSurvey={client.intakeSurvey}
        onCancel={onCancelEdit}
        onSave={onSave}
        onValidChange={onValidChange}
      />
    );
  }

  return <ClientDataDescriptionList isLoading={false} />;
}

interface DetailRowProps {
  label: string;
  value: string | React.ReactNode;
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value }) => (
  <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
    <dt className="text-sm font-medium leading-6 text-gray-900">{label}</dt>
    <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">{value}</dd>
  </div>
);

function ClientDataDescriptionList({ isLoading }: { isLoading: boolean }) {
  const client = useSelector(selectMaybeClient);
  const typeformPayload = useSelector(selectTypeformPayload);

  // Parse and merge typeform data with intakeSurvey
  const mergedSurveyData = useMemo(() => {
    if (!client?.intakeSurvey) {
      return null;
    }

    const merged = { ...client.intakeSurvey } as Record<string, any>;

    // Only merge if typeformPayload belongs to this client
    if (typeformPayload?.typeformPayload && typeformPayload.clientId === client.clientId) {
      try {
        const parsed = JSON.parse(typeformPayload.typeformPayload);

        Object.keys(parsed).forEach(key => {
          // Extract field name (before ::)
          const fieldName = key.split('::')[0];
          const schemaEntry = (clientIntakeFormSchema as Record<string, any>)[fieldName];
          if (!schemaEntry) return;

          // For list-type fields, prefer DB value when it's a non-empty array (includes "other" values)
          const existingValue = client.intakeSurvey[fieldName as keyof typeof client.intakeSurvey];
          if (
            schemaEntry.type === 'list' &&
            isArray(existingValue) &&
            existingValue.length > 0
          ) {
            merged[fieldName] = existingValue;
          } else if (
            fieldName !== 'clientHeightInches' &&
            (QUICK_EDIT_ALL_FIELDS as readonly string[]).includes(fieldName) &&
            existingValue != null
          ) {
            // For quick-edit fields, prefer DB values (they may have been updated via quick edit)
            merged[fieldName] = existingValue;
          } else {
            merged[fieldName] = parsed[key];
          }
        });
      } catch (error) {
        console.error('Error parsing typeformPayload:', error);
      }
    }

    return merged;
  }, [client?.intakeSurvey, client?.clientId, typeformPayload]);

  if (isLoading || !client) {
    return (
      <div className="my-10 flex h-64 items-center justify-center">
        <LoadingSkeleton />
      </div>
    );
  }

  const { intakeSurvey, introIntakeSurvey } = client;

  const formatValue = (value: any, type: string) => {
    switch (type) {
      case 'string':
        return value || 'N/A';
      case 'integer':
        return value ? value.toString() : 'N/A';
      case 'number':
        return value ? `$${parseFloat(value).toLocaleString()}` : 'N/A';
      case 'percent':
        return value ? `${parseFloat(value)}%` : 'N/A';
      case 'list':
        if (!value) return 'None';
        // If it's an array, use as is; if it's a string, split by delimiter (|||)
        const arr = isArray(value)
          ? value
          : typeof value === 'string'
            ? value.split('|||').map(item => item.trim())
            : [value];
        if (arr.length < 1) return 'None';
        return (
          <ul className="list-inside list-disc">
            {arr.map((item: any, index: number) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        );
      case 'date':
        if (!value) return 'N/A';
        const date = new Date(value);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          timeZone: 'UTC',
        });
      default:
        return 'N/A';
    }
  };

  const renderSurveyDetails = <T extends Record<string, any>>(surveyData: T) => {
    const sortedDetails = Object.entries(clientIntakeFormSchema)
      .filter(([key]) => key in surveyData) // Ensures we only include fields present in surveyData
      .sort((a, b) => a[1].group.localeCompare(b[1].group));

    return sortedDetails.map(([key, { label, type }]) => (
      <DetailRow
        key={key}
        label={label}
        value={formatValue(surveyData[key as keyof T], type)}
      />
    ));
  };

  return (
    <div
      data-hj-suppress
      className="mt-6 "
      data-private
    >
      {introIntakeSurvey && (
        <>
          <h2 className="text-base font-semibold text-gray-700">Client Information</h2>
          <dl className="divide-y divide-gray-100">
            {renderSurveyDetails<IntroIntakeSurvey>(introIntakeSurvey)}
          </dl>
        </>
      )}
      <h2 className="mt-6 text-base font-semibold text-gray-700">Intake Survey Answers</h2>
      <dl
        data-testid="survey-details-renderer"
        className="divide-y divide-gray-100"
      >
        {(mergedSurveyData || intakeSurvey) ? renderSurveyDetails<IntakeSurvey>((mergedSurveyData || intakeSurvey) as IntakeSurvey) : (
          <p className="py-4 text-sm text-gray-500">No intake survey data available.</p>
        )}
      </dl>
    </div>
  );
}

const LoadingSkeleton: React.FC = () => {
  return (
    <div
      role="status"
      className="w-full animate-pulse space-y-4 divide-y divide-gray-200 rounded  p-4  md:p-6 "
    >
      {[...Array(5)].map((_, index) => (
        <div
          key={index}
          className="flex items-center justify-between pt-4 first:pt-0"
        >
          <div>
            <div className="mb-2.5 h-2.5 w-24 rounded-full bg-gray-300 "></div>
            <div className="h-2 w-32 rounded-full bg-gray-200 "></div>
          </div>
          <div className="h-2.5 w-12 rounded-full bg-gray-300 "></div>
        </div>
      ))}
      <span className="sr-only">Loading...</span>
    </div>
  );
};
